/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { injectIntl } from 'react-intl'
import { flatMap, uniqBy, sortBy, partition } from 'lodash'

import { EuiFlexGroup, EuiFlexItem } from '@elastic/eui'

import type { Allocator, AllocatorSearchResult, AllocatorVizInstance } from '@modules/ui-types'

import { getSliderPrettyName } from '@/lib/sliders/messages'
import { getSupportedSliderInstanceTypes } from '@/lib/sliders/support'
import { useSliderColorPicker } from '@/lib/sliders/colors'

import { getMemoryCapacity } from '../../lib/allocatorInstanceCapacitiesViz'
import prettySize from '../../lib/prettySize'

import { useCapacityColorPicker } from './capacityVizColors'

import type { CapacityColorGetter } from './capacityVizColors'
import type { IntlShape } from 'react-intl'
import type { FunctionComponent } from 'react'

import './allocatorsTable-capacityVizLegend.scss'

type AllocatorViz = Allocator | AllocatorSearchResult

type CapacityColor = {
  memoryCapacity: number
  color: string
  title: string
}

type Props = {
  allocators: AllocatorViz[]
  intl: IntlShape
}

const InstanceCapacitiesVizLegend: FunctionComponent<Props> = ({
  allocators,
  intl: { formatMessage },
}) => {
  const instances = flatMap<AllocatorViz[], AllocatorVizInstance>(
    allocators,
    (allocator: AllocatorViz) => allocator.instances,
  )

  const getSliderColor = useSliderColorPicker()
  const getCapacityColor = useCapacityColorPicker()

  const unknownColor = getCapacityColor()

  const capacityColors = getCapacityColors(instances, getCapacityColor, unknownColor)

  return (
    <EuiFlexGroup gutterSize='s' alignItems='center' justifyContent='flexEnd' wrap={true}>
      {capacityColors.map(({ memoryCapacity, color, title }) => (
        <EuiFlexItem key={memoryCapacity} grow={false}>
          <span className='allocatorCapacityLegend-item'>
            <span className='allocatorCapacityLegend-color' style={{ backgroundColor: color }} />
            {` `}
            <span className={`allocatorCapacityLegend-title`}>{title}</span>
          </span>
        </EuiFlexItem>
      ))}

      {getInstanceKinds(instances).map(({ kind, title }) => (
        <EuiFlexItem key={kind} grow={false}>
          <span className='allocatorCapacityLegend-item'>
            <span
              className={`allocatorCapacityLegend-kind allocatorCapacityKind`}
              style={{ backgroundColor: getSliderColor(kind) }}
            />
            {` `}
            <span className='allocatorCapacityLegend-title'>{formatMessage(title)}</span>
          </span>
        </EuiFlexItem>
      ))}
    </EuiFlexGroup>
  )
}

export default injectIntl(InstanceCapacitiesVizLegend)

function getCapacityColors(
  instances: AllocatorVizInstance[],
  getCapacityColor: CapacityColorGetter,
  unknownColor: string,
): CapacityColor[] {
  const capacityColors: CapacityColor[] = uniqBy(instances, getMemoryCapacity)
    .map(getMemoryCapacity)
    .map((memoryCapacity) => ({
      memoryCapacity,
      color: getCapacityColor(memoryCapacity),
      title: prettySize(memoryCapacity),
    }))

  const unknownCapacity: CapacityColor = {
    memoryCapacity: -Infinity,
    color: unknownColor,
    title: `Other`,
  }
  const isKnownColor = (key: CapacityColor) => key.color !== unknownColor

  const [knownCapacities, unknownCapacities] = partition(capacityColors, isKnownColor)

  const normalizedCapacities =
    unknownCapacities.length > 0 ? [...knownCapacities, unknownCapacity] : capacityColors

  return sortBy(normalizedCapacities, (item) => -item.memoryCapacity)
}

function getInstanceKinds(instances: AllocatorVizInstance[]) {
  const allKinds = getSupportedSliderInstanceTypes().map((sliderInstanceType) => ({
    kind: sliderInstanceType,
    title: getSliderPrettyName({ sliderInstanceType }),
  }))

  return allKinds.filter((item) => instances.some(matchesKind(item.kind)))
}

function matchesKind(kind: AllocatorVizInstance['kind']) {
  return (instance: AllocatorVizInstance): boolean => instance.kind === kind
}
